// src/pages/itsm/ServiceCatalog.tsx
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Sun, Moon, Clock } from 'lucide-react'
// @ts-ignore
import { supabase } from '../../supabaseClient'
import { useTheme } from '../../hooks/useTheme'
import { T, Card, Pill, EmptyState, Modal, ConfirmDialog, showToast, ToastHost } from '../../components/ui/UI'

export default function ServiceCatalog() {
    const navigate = useNavigate()
    const { theme, toggleTheme } = useTheme()
    const [categories, setCategories] = useState<any[]>([])
    const [services, setServices] = useState<any[]>([])
    const [selectedCategory, setSelectedCategory] = useState<any>(null)
    const [showAddCategory, setShowAddCategory] = useState(false)
    const [showAddService, setShowAddService] = useState(false)
    const [editingService, setEditingService] = useState<any>(null)
    const [loading, setLoading] = useState(true)
    const [deleteTarget, setDeleteTarget] = useState<number | null>(null)

    const [catForm, setCatForm] = useState({ name: '', description: '', icon: '📋', color: '#2563eb' })
    const [svcForm, setSvcForm] = useState({
        name: '', description: '', sla_hours: 24, priority: 'medium',
        requires_approval: false, is_active: true
    })

    useEffect(() => { fetchAll() }, [])

    const fetchAll = async () => {
        const [{ data: cats }, { data: svcs }] = await Promise.all([
            supabase.from('itsm_service_categories').select('*').order('sort_order'),
            supabase.from('itsm_services').select('*, itsm_service_categories(name)').order('sort_order'),
        ])
        setCategories(cats || [])
        setServices(svcs || [])
        if (cats?.length && !selectedCategory) setSelectedCategory(cats[0])
        setLoading(false)
    }

    const saveCategory = async () => {
        await supabase.from('itsm_service_categories').insert([catForm])
        setCatForm({ name: '', description: '', icon: '📋', color: '#2563eb' })
        setShowAddCategory(false)
        showToast('Category saved', 'success')
        fetchAll()
    }

    const saveService = async () => {
        if (editingService) {
            await supabase.from('itsm_services').update(svcForm).eq('id', editingService.id)
            setEditingService(null)
        } else {
            await supabase.from('itsm_services').insert([{ ...svcForm, category_id: selectedCategory?.id }])
        }
        setSvcForm({ name: '', description: '', sla_hours: 24, priority: 'medium', requires_approval: false, is_active: true })
        setShowAddService(false)
        showToast('Service saved', 'success')
        fetchAll()
    }

    const deleteService = async (id: number) => {
        await supabase.from('itsm_services').delete().eq('id', id)
        showToast('Service deleted', 'success')
        fetchAll()
    }

    const toggleService = async (id: number, is_active: boolean) => {
        await supabase.from('itsm_services').update({ is_active: !is_active }).eq('id', id)
        fetchAll()
    }

    const filteredServices = selectedCategory
        ? services.filter(s => s.category_id === selectedCategory.id)
        : services

    const PRIORITY_TONE: Record<string, 'accent' | 'warning' | 'danger' | 'violet'> = {
        low: 'accent', medium: 'warning', high: 'danger', urgent: 'violet'
    }
    const priorityColor = (p: string) => {
        const tone = PRIORITY_TONE[p]
        return tone === 'accent' ? T.text2 : (T as any)[tone]
    }

    const s = styles

    if (loading) return <div className="jr-app" data-theme={theme} style={s.loading}>Loading...</div>

    return (
        <div className="jr-app" data-theme={theme} style={s.page}>
            <ToastHost />
            <button onClick={() => navigate('/dashboard/itsm')} style={s.back}>
                <ArrowLeft size={13} /> Back to ITSM
            </button>
            <div style={s.header}>
                <div>
                    <h1 style={s.title}>Service Catalog</h1>
                    <p style={s.subtitle}>{categories.length} categories · {services.length} services</p>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <button
                        onClick={toggleTheme}
                        aria-label="Toggle dark mode"
                        style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, border: `1px solid ${T.borderStrong}`, background: T.surface, color: T.text2, cursor: 'pointer' }}
                    >
                        {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
                    </button>
                    <button style={s.btnSecondary} onClick={() => setShowAddCategory(true)}>+ Category</button>
                    <button style={s.btnPrimary} onClick={() => setShowAddService(true)}>+ Service</button>
                </div>
            </div>

            <div style={s.layout}>
                {/* Categories Sidebar */}
                <Card style={{ padding: 16 }}>
                    <p style={s.sidebarTitle}>Categories</p>
                    {categories.map(cat => (
                        <div key={cat.id}
                            style={{ ...s.catItem, background: selectedCategory?.id === cat.id ? T.accentSoft : 'transparent', borderLeft: `3px solid ${selectedCategory?.id === cat.id ? cat.color : 'transparent'}` }}
                            onClick={() => setSelectedCategory(cat)}>
                            <span style={{ fontSize: 18 }}>{cat.icon}</span>
                            <div>
                                <p style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{cat.name}</p>
                                <p style={{ fontSize: 11, color: T.text3 }}>
                                    {services.filter(s => s.category_id === cat.id).length} services
                                </p>
                            </div>
                        </div>
                    ))}
                </Card>

                {/* Services List */}
                <div style={{ flex: 1 }}>
                    {selectedCategory && (
                        <div style={{ marginBottom: 16, padding: '12px 16px', background: selectedCategory.color + '15', borderRadius: 10, border: `1px solid ${selectedCategory.color}30` }}>
                            <p style={{ fontSize: 16, fontWeight: 700, color: T.text }}>
                                {selectedCategory.icon} {selectedCategory.name}
                            </p>
                            <p style={{ fontSize: 12, color: T.text2 }}>{selectedCategory.description}</p>
                        </div>
                    )}

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {filteredServices.map(svc => (
                            <Card key={svc.id} style={{ opacity: svc.is_active ? 1 : 0.5, display: 'flex', alignItems: 'center', gap: 16 }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                                        <p style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{svc.name}</p>
                                        {svc.requires_approval && (
                                            <Pill tone="violet" icon={undefined} style={undefined}>APPROVAL REQUIRED</Pill>
                                        )}
                                        {!svc.is_active && (
                                            <Pill tone="neutral" icon={undefined} style={undefined}>INACTIVE</Pill>
                                        )}
                                    </div>
                                    <p style={{ fontSize: 12, color: T.text2, marginBottom: 8 }}>{svc.description}</p>
                                    <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                                        <span style={{ fontSize: 11, color: T.text2, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                            <Clock size={11} /> SLA: {svc.sla_hours}h
                                        </span>
                                        <span style={{ fontSize: 11, fontWeight: 700, color: priorityColor(svc.priority) }}>
                                            {svc.priority.toUpperCase()}
                                        </span>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button style={s.btnSmall} onClick={() => {
                                        setEditingService(svc)
                                        setSvcForm({ name: svc.name, description: svc.description, sla_hours: svc.sla_hours, priority: svc.priority, requires_approval: svc.requires_approval, is_active: svc.is_active })
                                        setShowAddService(true)
                                    }}>Edit</button>
                                    <button style={s.btnSmall} onClick={() => toggleService(svc.id, svc.is_active)}>
                                        {svc.is_active ? 'Disable' : 'Enable'}
                                    </button>
                                    <button style={{ ...s.btnSmall, color: T.danger }} onClick={() => setDeleteTarget(svc.id)}>Delete</button>
                                </div>
                            </Card>
                        ))}
                        {filteredServices.length === 0 && (
                            <EmptyState title='No services yet' sub='Click "+ Service" to add one' icon={undefined} />
                        )}
                    </div>
                </div>
            </div>

            {/* Add Category Modal */}
            <Modal open={showAddCategory} onClose={() => setShowAddCategory(false)} title="New Category"
                footer={
                    <>
                        <button style={s.btnOutline} onClick={() => setShowAddCategory(false)}>Cancel</button>
                        <button style={s.btnPrimary} onClick={saveCategory}>Save</button>
                    </>
                }>
                <div style={s.field}>
                    <label style={s.label}>Name *</label>
                    <input style={s.input} value={catForm.name} onChange={e => setCatForm({ ...catForm, name: e.target.value })} placeholder="IT Support" />
                </div>
                <div style={s.field}>
                    <label style={s.label}>Description</label>
                    <input style={s.input} value={catForm.description} onChange={e => setCatForm({ ...catForm, description: e.target.value })} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={s.field}>
                        <label style={s.label}>Icon (emoji)</label>
                        <input style={s.input} value={catForm.icon} onChange={e => setCatForm({ ...catForm, icon: e.target.value })} />
                    </div>
                    <div style={s.field}>
                        <label style={s.label}>Color</label>
                        <input type="color" style={{ ...s.input, padding: 4, height: 36 }} value={catForm.color} onChange={e => setCatForm({ ...catForm, color: e.target.value })} />
                    </div>
                </div>
            </Modal>

            {/* Add/Edit Service Modal */}
            <Modal open={showAddService} onClose={() => { setShowAddService(false); setEditingService(null) }} title={editingService ? 'Edit Service' : 'New Service'}
                footer={
                    <>
                        <button style={s.btnOutline} onClick={() => { setShowAddService(false); setEditingService(null) }}>Cancel</button>
                        <button style={s.btnPrimary} onClick={saveService}>Save</button>
                    </>
                }>
                <div style={s.field}>
                    <label style={s.label}>Service Name *</label>
                    <input style={s.input} value={svcForm.name} onChange={e => setSvcForm({ ...svcForm, name: e.target.value })} placeholder="Password Reset" />
                </div>
                <div style={s.field}>
                    <label style={s.label}>Description</label>
                    <textarea style={{ ...s.input, height: 70, padding: '8px 10px', resize: 'none' as const }} value={svcForm.description} onChange={e => setSvcForm({ ...svcForm, description: e.target.value })} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={s.field}>
                        <label style={s.label}>SLA Hours</label>
                        <input type="number" style={s.input} value={svcForm.sla_hours} onChange={e => setSvcForm({ ...svcForm, sla_hours: Number(e.target.value) })} />
                    </div>
                    <div style={s.field}>
                        <label style={s.label}>Priority</label>
                        <select style={s.input} value={svcForm.priority} onChange={e => setSvcForm({ ...svcForm, priority: e.target.value })}>
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="urgent">Urgent</option>
                        </select>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 14 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.text2, cursor: 'pointer' }}>
                        <input type="checkbox" checked={svcForm.requires_approval} onChange={e => setSvcForm({ ...svcForm, requires_approval: e.target.checked })} />
                        Requires Approval
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.text2, cursor: 'pointer' }}>
                        <input type="checkbox" checked={svcForm.is_active} onChange={e => setSvcForm({ ...svcForm, is_active: e.target.checked })} />
                        Active
                    </label>
                </div>
            </Modal>

            <ConfirmDialog
                open={deleteTarget !== null}
                onClose={() => setDeleteTarget(null)}
                onConfirm={() => { if (deleteTarget !== null) deleteService(deleteTarget) }}
                title="Delete service?"
                message="This will permanently remove this service from the catalog."
                confirmLabel="Delete"
                danger
            />
        </div>
    )
}

const styles: Record<string, React.CSSProperties> = {
    page: { padding: '32px 24px', maxWidth: 1200, margin: '0 auto' },
    loading: { textAlign: 'center', padding: '60px', color: T.text3 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 },
    title: { fontSize: 24, fontWeight: 700, color: T.text, marginBottom: 4 },
    back: { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: T.info, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0, fontWeight: 500 },
    subtitle: { fontSize: 13, color: T.text2 },
    layout: { display: 'grid', gridTemplateColumns: '240px 1fr', gap: 20 },
    sidebarTitle: { fontSize: 11, fontWeight: 700, color: T.text3, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 },
    catItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 4 },
    btnPrimary: { height: 36, padding: '0 18px', background: T.accent, color: T.accentFg, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
    btnSecondary: { height: 36, padding: '0 18px', background: 'transparent', color: T.text2, border: `1px solid ${T.borderStrong}`, borderRadius: 8, fontSize: 13, cursor: 'pointer' },
    btnOutline: { flex: 1, height: 36, background: 'transparent', color: T.text2, border: `1px solid ${T.borderStrong}`, borderRadius: 8, fontSize: 13, cursor: 'pointer' },
    btnSmall: { height: 30, padding: '0 12px', background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 6, fontSize: 12, cursor: 'pointer', color: T.text2 },
    field: { marginBottom: 14 },
    label: { display: 'block', fontSize: 12, fontWeight: 500, color: T.text2, marginBottom: 5 },
    input: { width: '100%', height: 36, border: `1px solid ${T.borderStrong}`, borderRadius: 8, padding: '0 10px', fontSize: 13, color: T.text, outline: 'none', background: T.surface },
}
